import { useState, useEffect } from 'react';
import axios from 'axios';
import { RefreshCw, CheckCircle2, XCircle, Server, Database, Layers, Globe, ShieldCheck, Lock, KeyRound, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '../store';
import toast from 'react-hot-toast';

interface StatusData {
  status: string;
  timestamp: string;
  uptimeSeconds: number;
  database: {
    connected: boolean;
    provider: string;
    categoriesCount: number;
    productsCount: number;
    ordersCount: number;
    error: string | null;
  };
  redis: {
    connected: boolean;
    error: string | null;
  };
  api: {
    status: string;
    version: string;
    port: number;
  };
}

export default function StatusPage() {
  const { user } = useAuthStore();
  const isAdmin = user?.role === 'Admin' || user?.role === 'Operator';

  const [isUnlocked, setIsUnlocked] = useState<boolean>(() => {
    return isAdmin || sessionStorage.getItem('status_pin_auth') === 'verified';
  });

  const [pinInput, setPinInput] = useState('');
  const [pinError, setPinError] = useState('');

  const [data, setData] = useState<StatusData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastCheck, setLastCheck] = useState<Date>(new Date());

  const checkStatus = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('access_token');
      const res = await axios.get('http://localhost:5000/api/status', {
        timeout: 3000,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setData(res.data);
      setLastCheck(new Date());
    } catch (err: any) {
      setError(err.message || 'Backend API sunucusuna ulaşılamadı (localhost:5000 kapalı).');
      setData(null);
      setLastCheck(new Date());
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) {
      setIsUnlocked(true);
    }
  }, [isAdmin]);

  useEffect(() => {
    if (isUnlocked) {
      checkStatus();
      const interval = setInterval(checkStatus, 10000);
      return () => clearInterval(interval);
    }
  }, [isUnlocked]);

  const handlePinSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (pinInput === 'admin123') {
      sessionStorage.setItem('status_pin_auth', 'verified');
      setIsUnlocked(true);
      toast.success('Güvenlik doğrulaması başarılı!');
    } else {
      setPinError('Hatalı güvenlik şifresi!');
      toast.error('Yetkisiz erişim: Şifre hatalı!');
    }
  };

  const handleLock = () => {
    sessionStorage.removeItem('status_pin_auth');
    setIsUnlocked(false);
    setPinInput('');
    toast.success('Oturum kilitlendi');
  };

  const formatUptime = (seconds?: number) => {
    if (!seconds && seconds !== 0) return '-';
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hrs}s ${mins}d ${secs}sn`;
  };

  // Korumalı Ekran (PIN / Şifre Girişi)
  if (!isUnlocked) {
    return (
      <div style={{ minHeight: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
        <div className="card animate-fade-in" style={{ width: '100%', maxWidth: '420px', padding: '36px', textAlign: 'center' }}>
          <div style={{ width: '56px', height: '56px', borderRadius: '50%', background: 'rgba(255, 94, 20, 0.1)', color: 'var(--color-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
            <Lock size={28} />
          </div>
          <h1 style={{ fontSize: '1.4rem', fontWeight: 800, marginBottom: '8px' }}>Güvenli Durum Paneli</h1>
          <p className="text-muted" style={{ fontSize: '0.85rem', marginBottom: '24px', lineHeight: 1.5 }}>
            Bu sayfa sistem ve veritabanı altyapı metriklerini içerir. Görüntülemek için yönetici güvenlik şifresini girin.
          </p>

          <form onSubmit={handlePinSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div style={{ position: 'relative' }}>
              <KeyRound size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--color-text-secondary)' }} />
              <input
                type="password"
                className="form-input"
                style={{ paddingLeft: '38px', width: '100%', textAlign: 'center', letterSpacing: '0.2em', fontSize: '1.1rem' }}
                placeholder="••••••••"
                required
                autoFocus
                value={pinInput}
                onChange={(e) => { setPinInput(e.target.value); setPinError(''); }}
              />
            </div>

            {pinError && (
              <span style={{ color: 'var(--color-error)', fontSize: '0.85rem', fontWeight: 600 }}>
                {pinError}
              </span>
            )}

            <button type="submit" className="btn btn-primary btn-full" style={{ padding: '12px' }}>
              Doğrula ve Giriş Yap
            </button>
          </form>

          <div style={{ marginTop: '20px', fontSize: '0.85rem' }}>
            <Link to="/" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', color: 'var(--color-text-secondary)' }}>
              <ArrowLeft size={14} /> Ana Sayfaya Dön
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '960px', margin: '40px auto', padding: '0 20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <h1 style={{ fontSize: '1.8rem', fontWeight: 800, margin: 0 }}>Sistem Durumu</h1>
            <span style={{ background: 'rgba(16, 185, 129, 0.1)', color: 'var(--color-success)', padding: '2px 8px', borderRadius: '6px', fontSize: '0.75rem', fontWeight: 700 }}>
              KORUMALI
            </span>
          </div>
          <p className="text-muted" style={{ fontSize: '0.9rem', margin: '4px 0 0' }}>
            İzavadak sunucu, veritabanı ve servislerin anlık çalışma durumu
          </p>
        </div>

        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            className="btn btn-secondary btn-sm"
            onClick={checkStatus}
            disabled={loading}
            style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            {loading ? 'Kontrol...' : 'Yenile'}
          </button>
          <button
            className="btn btn-ghost btn-sm"
            onClick={handleLock}
            title="Paneli Kilitle"
            style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
          >
            <Lock size={14} /> Kilitle
          </button>
        </div>
      </div>

      {/* Genel Özet Kartı */}
      <div
        className="card"
        style={{
          padding: '24px',
          marginBottom: '24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '16px',
          borderLeft: `5px solid ${data?.status === 'Healthy' ? 'var(--color-success)' : 'var(--color-error)'}`
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          {data?.status === 'Healthy' ? (
            <CheckCircle2 size={36} color="var(--color-success)" />
          ) : (
            <XCircle size={36} color="var(--color-error)" />
          )}
          <div>
            <h2 style={{ fontSize: '1.25rem', fontWeight: 700, margin: 0 }}>
              {data?.status === 'Healthy' ? 'Tüm Sistemler Sorunsuz Çalışıyor' : 'Sistemde Sorun Tespit Edildi'}
            </h2>
            <p className="text-muted" style={{ fontSize: '0.85rem', margin: '4px 0 0' }}>
              Son kontrol: {lastCheck.toLocaleTimeString('tr-TR')} (Her 10 saniyede bir güncellenir)
            </p>
          </div>
        </div>
        {data && (
          <span style={{ fontSize: '0.85rem', color: 'var(--color-text-secondary)', background: 'var(--color-surface-2)', padding: '6px 12px', borderRadius: '8px' }}>
            Çalışma Süresi: <strong>{formatUptime(data.uptimeSeconds)}</strong>
          </span>
        )}
      </div>

      {/* Detay Kartları */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '16px' }}>
        {/* Backend API */}
        <div className="card" style={{ padding: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '14px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Server size={22} color="var(--color-primary)" />
              <strong style={{ fontSize: '1rem' }}>Backend API</strong>
            </div>
            {data ? (
              <span style={{ background: 'rgba(16, 185, 129, 0.1)', color: 'var(--color-success)', padding: '4px 8px', borderRadius: '6px', fontSize: '0.8rem', fontWeight: 600 }}>
                ● Çalışıyor
              </span>
            ) : (
              <span style={{ background: 'rgba(239, 68, 68, 0.1)', color: 'var(--color-error)', padding: '4px 8px', borderRadius: '6px', fontSize: '0.8rem', fontWeight: 600 }}>
                ● Kapalı
              </span>
            )}
          </div>
          <div style={{ fontSize: '0.85rem', color: 'var(--color-text-secondary)', display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <div>Port: <strong>5000</strong> (ASP.NET Core 9)</div>
            <div>URL: <a href="http://localhost:5000/swagger" target="_blank" rel="noreferrer" style={{ color: 'var(--color-primary)' }}>http://localhost:5000/swagger</a></div>
            {error && <div style={{ color: 'var(--color-error)', marginTop: '6px', fontSize: '0.8rem' }}>Hata: {error}</div>}
          </div>
        </div>

        {/* PostgreSQL Veritabanı */}
        <div className="card" style={{ padding: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '14px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Database size={22} color="var(--color-primary)" />
              <strong style={{ fontSize: '1rem' }}>PostgreSQL</strong>
            </div>
            {data?.database.connected ? (
              <span style={{ background: 'rgba(16, 185, 129, 0.1)', color: 'var(--color-success)', padding: '4px 8px', borderRadius: '6px', fontSize: '0.8rem', fontWeight: 600 }}>
                ● Bağlı
              </span>
            ) : (
              <span style={{ background: 'rgba(239, 68, 68, 0.1)', color: 'var(--color-error)', padding: '4px 8px', borderRadius: '6px', fontSize: '0.8rem', fontWeight: 600 }}>
                ● Bağlantı Yok
              </span>
            )}
          </div>
          <div style={{ fontSize: '0.85rem', color: 'var(--color-text-secondary)', display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <div>Port: <strong>5432</strong> (izavadak_dev)</div>
            <div>Kategori Sayısı: <strong>{data?.database.categoriesCount ?? '-'}</strong></div>
            <div>Kayıtlı Yemek/Ürün: <strong>{data?.database.productsCount ?? '-'}</strong></div>
            <div>Toplam Sipariş: <strong>{data?.database.ordersCount ?? '-'}</strong></div>
            {data?.database.error && <div style={{ color: 'var(--color-error)' }}>{data.database.error}</div>}
          </div>
        </div>

        {/* Redis Cache */}
        <div className="card" style={{ padding: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '14px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Layers size={22} color="var(--color-primary)" />
              <strong style={{ fontSize: '1rem' }}>Redis Cache</strong>
            </div>
            {data?.redis.connected ? (
              <span style={{ background: 'rgba(16, 185, 129, 0.1)', color: 'var(--color-success)', padding: '4px 8px', borderRadius: '6px', fontSize: '0.8rem', fontWeight: 600 }}>
                ● Bağlı
              </span>
            ) : (
              <span style={{ background: 'rgba(239, 68, 68, 0.1)', color: 'var(--color-error)', padding: '4px 8px', borderRadius: '6px', fontSize: '0.8rem', fontWeight: 600 }}>
                ● Bağlantı Yok
              </span>
            )}
          </div>
          <div style={{ fontSize: '0.85rem', color: 'var(--color-text-secondary)', display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <div>Port: <strong>6379</strong> (Docker)</div>
            <div>Önbellek & SignalR: {data?.redis.connected ? 'Aktif' : 'Devre Dışı'}</div>
            {data?.redis.error && <div style={{ color: 'var(--color-error)' }}>{data.redis.error}</div>}
          </div>
        </div>

        {/* Frontend Web */}
        <div className="card" style={{ padding: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '14px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Globe size={22} color="var(--color-primary)" />
              <strong style={{ fontSize: '1rem' }}>Frontend</strong>
            </div>
            <span style={{ background: 'rgba(16, 185, 129, 0.1)', color: 'var(--color-success)', padding: '4px 8px', borderRadius: '6px', fontSize: '0.8rem', fontWeight: 600 }}>
              ● Çalışıyor
            </span>
          </div>
          <div style={{ fontSize: '0.85rem', color: 'var(--color-text-secondary)', display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <div>Port: <strong>5173</strong> (Vite + React)</div>
            <div>URL: <strong>http://localhost:5173</strong></div>
          </div>
        </div>
      </div>

      {/* Bilgilendirme Kutusu */}
      <div style={{ marginTop: '24px', padding: '16px', background: 'var(--color-surface-2)', borderRadius: '12px', border: '1px solid var(--color-border)', fontSize: '0.85rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px', fontWeight: 600 }}>
          <ShieldCheck size={18} color="var(--color-primary)" /> Güvenlik & Geliştirme Notu:
        </div>
        <p className="text-muted" style={{ margin: 0 }}>
          Bu panel korumalıdır. Yetkisiz kullanıcılar veritabanı veya sunucu durumunu göremez. Veritabanındaki tüm yemekler ve siparişler kalıcı disk biriminde güvenle saklanmaktadır.
        </p>
      </div>
    </div>
  );
}
